import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Card, CardBody, Button, Row, Col, Alert } from "reactstrap";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useNavigate } from "react-router-dom";
import { Save, ArrowLeft, RefreshCw, CheckCircle, AlertTriangle } from "react-feather";

function UpdateGlobalOptions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [financialYears, setFinancialYears] = useState([]);
  const [alertInfo, setAlertInfo] = useState({ show: false, type: "", message: "" });

  const [formData, setFormData] = useState({
    CompanyName: "KirayaGo",
    F_FinancialYearMaster: "1",
    RequestOnPortal: false,
    DriverSearchRadiusKM: 5.0,
    ReachedThresholdMeters: 100.0,
    UserAppVersionCode: 22,
    CaptainAppVersionCode: 17,
    UserAppPlayStoreUrl: "https://play.google.com/store/apps/details?id=com.KirayagoNew",
    CaptainAppPlayStoreUrl: "https://play.google.com/store/apps/details?id=com.KirayaGoCaptain",
  });

  // Fetch initial GlobalOptions and FinancialYearMaster
  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Financial Years
      try {
        const fyRes = await fetch(`${API_WEB_URLS.MASTER}/0/token/FinancialYearMaster/Id/0`);
        const fyJson = await fyRes.json();
        const fyList = fyJson?.dataList || fyJson?.DataList || fyJson?.data || (Array.isArray(fyJson) ? fyJson : []);
        if (Array.isArray(fyList)) {
          setFinancialYears(fyList);
        }
      } catch (e) {
        console.warn("Could not fetch financial years", e);
      }

      // 2. Fetch Global Options from API
      const optRes = await fetch(`${API_WEB_URLS.BASE}GlobalOption/Get`, {
        headers: { "Cache-Control": "no-cache" }
      });
      const optJson = await optRes.json();
      const list = optJson?.Data || optJson?.data || (Array.isArray(optJson) ? optJson : []);
      const item = Array.isArray(list) && list.length > 0 ? list[0] : (list && typeof list === "object" ? list : null);

      if (item) {
        setFormData({
          CompanyName: item.CompanyName || "KirayaGo",
          F_FinancialYearMaster: String(item.F_FinancialYearMaster || "1"),
          RequestOnPortal: Boolean(item.RequestOnPortal),
          DriverSearchRadiusKM: item.DriverSearchRadiusKM !== undefined && item.DriverSearchRadiusKM !== null ? Number(item.DriverSearchRadiusKM) : 5.0,
          ReachedThresholdMeters: item.ReachedThresholdMeters !== undefined && item.ReachedThresholdMeters !== null ? Number(item.ReachedThresholdMeters) : 100.0,
          UserAppVersionCode: item.UserAppVersionCode ? Number(item.UserAppVersionCode) : 22,
          CaptainAppVersionCode: item.CaptainAppVersionCode ? Number(item.CaptainAppVersionCode) : 17,
          UserAppPlayStoreUrl: item.UserAppPlayStoreUrl || "https://play.google.com/store/apps/details?id=com.KirayagoNew",
          CaptainAppPlayStoreUrl: item.CaptainAppPlayStoreUrl || "https://play.google.com/store/apps/details?id=com.KirayaGoCaptain",
        });
      }
    } catch (error) {
      console.error("Error loading GlobalOptions:", error);
      setAlertInfo({ show: true, type: "danger", message: "Failed to load global options. Please retry." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    CompanyName: Yup.string().required("Company name is required"),
    DriverSearchRadiusKM: Yup.number()
      .required("Driver search radius is required")
      .min(0.5, "Radius must be at least 0.5 KM")
      .max(100, "Radius cannot exceed 100 KM"),
    ReachedThresholdMeters: Yup.number()
      .required("Reached threshold distance is required")
      .min(10, "Reached threshold must be at least 10 meters")
      .max(5000, "Reached threshold cannot exceed 5000 meters"),
    CaptainAppVersionCode: Yup.number().required("Captain version is required").min(1),
    UserAppVersionCode: Yup.number().required("User version is required").min(1),
  });

  const handleSubmit = async (values) => {
    setSaving(true);
    setAlertInfo({ show: false, type: "", message: "" });
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const userId = authUser.uid || "0";
      const token = authUser.token || "token";

      const payload = {
        CompanyName: values.CompanyName,
        F_FinancialYearMaster: Number(values.F_FinancialYearMaster || 1),
        RequestOnPortal: Boolean(values.RequestOnPortal),
        DriverSearchRadiusKM: Number(values.DriverSearchRadiusKM),
        ReachedThresholdMeters: Number(values.ReachedThresholdMeters),
        UserAppVersionCode: Number(values.UserAppVersionCode),
        CaptainAppVersionCode: Number(values.CaptainAppVersionCode),
        UserAppPlayStoreUrl: values.UserAppPlayStoreUrl,
        CaptainAppPlayStoreUrl: values.CaptainAppPlayStoreUrl,
      };

      const response = await fetch(`${API_WEB_URLS.BASE}GlobalOption/${userId}/${token}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const resJson = await response.json();
      if (response.ok && (resJson?.Success !== false)) {
        setAlertInfo({
          show: true,
          type: "success",
          message: "✅ Global Options updated successfully! Driver App & Customer App will automatically use these settings."
        });
        setFormData(values);
      } else {
        throw new Error(resJson?.Message || "Failed to update options");
      }
    } catch (err) {
      console.error("Save error:", err);
      setAlertInfo({ show: true, type: "danger", message: `❌ Error: ${err.message || "Failed to update options"}` });
    } finally {
      setSaving(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3" style={{ maxWidth: 1100 }}>
      {/* Header Bar */}
      <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
        <div>
          <h5 className="mb-0 text-dark fw-bold">
            <i className="bx bx-slider-alt me-2 text-primary"></i>
            Global Options & Platform Settings
          </h5>
          <small className="text-muted">
            Configure live driver search range, arrival thresholds, and mobile app version controls.
          </small>
        </div>
        <div className="d-flex gap-2">
          <Button
            type="button"
            size="sm"
            color="light"
            onClick={loadData}
            disabled={saving}
            className="d-flex align-items-center"
          >
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
          <Button
            type="button"
            size="sm"
            color="secondary"
            onClick={() => navigate(-1)}
            className="d-flex align-items-center"
          >
            <ArrowLeft size={14} className="me-1" /> Back
          </Button>
          <Button
            type="submit"
            form="global-options-form"
            size="sm"
            color="primary"
            disabled={saving}
            className="d-flex align-items-center px-3 fw-bold"
          >
            <Save size={14} className="me-1" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {alertInfo.show && (
        <Alert color={alertInfo.type} className="shadow-sm mb-3">
          <div className="d-flex align-items-center">
            {alertInfo.type === "success" ? (
              <CheckCircle size={18} className="me-2 text-success" />
            ) : (
              <AlertTriangle size={18} className="me-2 text-danger" />
            )}
            <div>{alertInfo.message}</div>
          </div>
        </Alert>
      )}

      <Formik
        initialValues={formData}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched }) => (
          <Form id="global-options-form">
            <Row className="g-3">
              {/* 1. 🚖 Driver Dispatch & Distance Thresholds */}
              <Col lg={12}>
                <Card className="border shadow-sm mb-3">
                  <div className="card-header bg-soft-primary py-2 border-bottom">
                    <h6 className="mb-0 text-primary fw-bold">
                      <i className="bx bx-navigation me-2"></i>
                      1. Driver Dispatch & Distance Range Settings (Live App)
                    </h6>
                  </div>
                  <CardBody className="p-3">
                    <Row className="g-3">
                      {/* Driver Search Radius KM */}
                      <Col md={6}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          <i className="bx bx-radar me-1 text-primary"></i>
                          Driver Search / Nearby Alert Radius (in KM) <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <Field
                            type="number"
                            step="0.1"
                            name="DriverSearchRadiusKM"
                            className={`form-control ${errors.DriverSearchRadiusKM && touched.DriverSearchRadiusKM ? "is-invalid" : ""}`}
                            placeholder="e.g. 5.0"
                          />
                          <span className="input-group-text bg-light fw-bold">KM</span>
                        </div>
                        <small className="text-muted d-block mt-1">
                          Pickup location ke is radius (KM) ke daayre mein jitne online captains honge unko booking alert jayega.
                        </small>
                        <ErrorMessage name="DriverSearchRadiusKM" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      {/* Reached Threshold Distance Meters */}
                      <Col md={6}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          <i className="bx bx-map-pin me-1 text-success"></i>
                          Pickup & Drop "Reached" Threshold (in Meters) <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <Field
                            type="number"
                            step="1"
                            name="ReachedThresholdMeters"
                            className={`form-control ${errors.ReachedThresholdMeters && touched.ReachedThresholdMeters ? "is-invalid" : ""}`}
                            placeholder="e.g. 100"
                          />
                          <span className="input-group-text bg-light fw-bold">Meters</span>
                        </div>
                        <small className="text-muted d-block mt-1">
                          Captain jab pickup/drop location ke itne meters ke daayre mein pahunchega tab "Reached" button activate hoga.
                        </small>
                        <ErrorMessage name="ReachedThresholdMeters" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {/* 2. 📱 Mobile App Version Management */}
              <Col lg={12}>
                <Card className="border shadow-sm mb-3">
                  <div className="card-header bg-soft-warning py-2 border-bottom">
                    <h6 className="mb-0 text-warning fw-bold text-dark">
                      <i className="bx bx-mobile-alt me-2"></i>
                      2. Mobile App Version Control & Play Store Links
                    </h6>
                  </div>
                  <CardBody className="p-3">
                    <Row className="g-3">
                      {/* Captain App Version Code */}
                      <Col md={3}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          Captain App Min Version Code <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="number"
                          name="CaptainAppVersionCode"
                          className={`form-control ${errors.CaptainAppVersionCode && touched.CaptainAppVersionCode ? "is-invalid" : ""}`}
                          placeholder="e.g. 17"
                        />
                        <ErrorMessage name="CaptainAppVersionCode" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      {/* Captain App PlayStore URL */}
                      <Col md={9}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          Captain App Google Play Store URL
                        </label>
                        <Field
                          type="text"
                          name="CaptainAppPlayStoreUrl"
                          className="form-control"
                          placeholder="https://play.google.com/store/apps/details?id=com.KirayaGoCaptain"
                        />
                      </Col>

                      {/* User App Version Code */}
                      <Col md={3}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          User App Min Version Code <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="number"
                          name="UserAppVersionCode"
                          className={`form-control ${errors.UserAppVersionCode && touched.UserAppVersionCode ? "is-invalid" : ""}`}
                          placeholder="e.g. 22"
                        />
                        <ErrorMessage name="UserAppVersionCode" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      {/* User App PlayStore URL */}
                      <Col md={9}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          User App Google Play Store URL
                        </label>
                        <Field
                          type="text"
                          name="UserAppPlayStoreUrl"
                          className="form-control"
                          placeholder="https://play.google.com/store/apps/details?id=com.KirayagoNew"
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {/* 3. 🏢 Company & Portal Defaults */}
              <Col lg={12}>
                <Card className="border shadow-sm mb-3">
                  <div className="card-header bg-soft-info py-2 border-bottom">
                    <h6 className="mb-0 text-info fw-bold text-dark">
                      <i className="bx bx-building me-2"></i>
                      3. Company Profile & Financial Year
                    </h6>
                  </div>
                  <CardBody className="p-3">
                    <Row className="g-3">
                      <Col md={6}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          Company / Firm Name <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="text"
                          name="CompanyName"
                          className={`form-control ${errors.CompanyName && touched.CompanyName ? "is-invalid" : ""}`}
                          placeholder="KirayaGo Logistics"
                        />
                        <ErrorMessage name="CompanyName" component="div" className="text-danger font-size-12 mt-1" />
                      </Col>

                      <Col md={6}>
                        <label className="form-label fw-semibold text-dark mb-1">
                          Active Financial Year <span className="text-danger">*</span>
                        </label>
                        <Field as="select" name="F_FinancialYearMaster" className="form-select">
                          <option value="1">FY 2026-27</option>
                          {financialYears.map((fy) => (
                            <option key={fy.Id} value={fy.Id}>{fy.Name}</option>
                          ))}
                        </Field>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Bottom Action Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-2 mb-4">
              <Button type="button" color="light" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={saving} className="px-4 fw-bold">
                <Save size={15} className="me-1" />
                {saving ? "Saving Changes..." : "Save Platform Settings"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default UpdateGlobalOptions;

